import { useMemo, useState, MouseEvent } from 'react';
import { Copy, CopyCheck, Download, Expand } from 'lucide-react';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import { CsvViewer } from 'src/shared/components/thread-store/csv/CsvViewer';
import { useExpandedView } from 'src/shared/components/thread-store/context/ExpandedViewContext';
import { PushCsvFileToOutlookApp as PushCsvFileToOutlookAppType } from 'src/features/flows/types/threadStore.types';
import DynamicIcon from 'src/shared/components/ui/icons/dynamic-icon';
import { Separator } from 'src/shared/components/ui/separator';
import WithTooltip from 'src/shared/components/utils/WithTooltip';
import { cn } from 'src/shared/lib/utils';

/**
 * Converts CSV string to TSV (tab-separated values) format
 */
function convertCsvToTsv(csvString: string): string {
  try {
    const parsed = Papa.parse<string[]>(csvString, {
      header: false,
      skipEmptyLines: false,
    });

    // Convert each row to tab-separated format
    return parsed.data.map((row) => row.join('\t')).join('\n');
  } catch (error) {
    console.error('Error converting CSV to TSV:', error);
    // Fallback: return original if conversion fails
    return csvString;
  }
}

interface CopyButtonProps {
  value: string;
  className?: string;
}

function CopyButton({ value, className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  async function handleCopy(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();

    // Convert CSV to TSV before copying
    const tsvValue = convertCsvToTsv(value);

    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(tsvValue);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch (err) {
      // Clipboard API failed (e.g., permissions policy in iframe)
      console.warn('Clipboard API failed, trying fallback:', err);
    }

    // Fallback: use older execCommand method
    try {
      const textArea = document.createElement('textarea');
      textArea.value = tsvValue;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.warn('execCommand copy failed');
      }
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }

  return (
    <button
      type="button"
      disabled={!value}
      onClick={handleCopy}
      className={cn(
        'shrink-0 disabled:opacity-50 disabled:pointer-events-none',
        !copied ? 'hover:text-neutral-800 text-neutral-400 transition-all' : '',
        className
      )}
    >
      {!copied ? <Copy size={12} /> : <CopyCheck size={12} className="text-success hover:text-success-800" />}
    </button>
  );
}

interface DownloadButtonProps {
  filename: string;
  file: string;
  className?: string;
}

function DownloadButton({ filename, file, className }: DownloadButtonProps) {
  function handleDownload(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    const blob = new Blob([file], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
  }

  return (
    <button
      type="button"
      disabled={!file}
      onClick={handleDownload}
      className={cn(
        'shrink-0 disabled:opacity-50 disabled:pointer-events-none hover:text-neutral-800 text-neutral-400 transition-all',
        className
      )}
    >
      <Download size={12} />
    </button>
  );
}

interface CsvFileCardProps {
  threadStore: PushCsvFileToOutlookAppType;
  /** Whether to show the download button. Default: true */
  showDownload?: boolean;
  /** Whether to show the expand button. Default: true */
  showExpand?: boolean;
}

/**
 * Card component for displaying CSV files pushed from automation flows.
 * Platform-agnostic - uses useExpandedView for opening the full view.
 */
export function CsvFileCard({ threadStore, showDownload = true, showExpand = true }: CsvFileCardProps) {
  const { openExpandedView } = useExpandedView();
  const { data } = threadStore;

  const { headers, dataRows } = useMemo(() => {
    if (!threadStore.data.file || typeof threadStore.data.file !== 'string') {
      return { headers: [], dataRows: [] };
    }

    try {
      const parsed = Papa.parse<string[]>(threadStore.data.file, {
        header: false,
        skipEmptyLines: true,
      });

      const allRows = parsed.data;

      if (!allRows.length) {
        return { headers: [], dataRows: [] };
      }

      if (data.containsHeader) {
        return {
          headers: allRows[0],
          dataRows: allRows.slice(1),
        };
      }

      return {
        headers: [],
        dataRows: allRows,
      };
    } catch (error) {
      console.error('Error parsing CSV:', error);
      return { headers: [], dataRows: [] };
    }
  }, [data.containsHeader, threadStore.data.file]);

  const handleExpand = () => {
    openExpandedView({
      type: 'csv-viewer',
      data: {
        threadStoreId: threadStore.id,
        integrationId: threadStore.integrationId,
        threadId: threadStore.threadId,
        title: data.title,
        file: data.file,
        filename: data.filename,
        containsHeader: data.containsHeader,
        editable: false,
      },
    });
  };

  return (
    <div className="flex flex-col border rounded-sm bg-white p-3 gap-2 w-full">
      <div className="flex items-center flex-wrap overflow-hidden gap-2 w-full h-6">
        <div className="flex-center shrink-0 rounded-sm w-7 h-full bg-dashboardblue-50">
          <DynamicIcon name="FileSpreadsheet" size={16} className="shrink-0 stroke-2 text-dashboardblue" />
        </div>
        <span className="text-sm font-bold">{data.title}</span>
        <div className="flex-grow" />
        <div className="flex gap-2 items-center">
          {showExpand && (
            <button
              type="button"
              onClick={handleExpand}
              aria-label="Expand CSV"
              className="shrink-0 hover:text-neutral-800 text-neutral-400 transition-all"
            >
              <Expand size={12} />
            </button>
          )}
          {showDownload && (
            <WithTooltip tooltip="Download CSV">
              <div>
                <DownloadButton filename={data.filename || data.title} file={data.file} />
              </div>
            </WithTooltip>
          )}
          <CopyButton value={data.file} />
        </div>
      </div>
      <Separator />
      {dataRows.length > 0 ? (
        <CsvViewer headers={headers} rows={dataRows} maxHeight="300px" />
      ) : (
        <span className="text-sm text-gray-500 p-3">No data to display.</span>
      )}
    </div>
  );
}

export default CsvFileCard;
